import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaSearch,
  FaCheck,
  FaCalendar,
  FaCalendarCheck,
  FaPlus,
  FaFilter,
  FaEye,
  FaMoneyBillWave,
  FaCreditCard,
  FaUniversity,
  FaShieldAlt,
  FaGlobe,
  FaFileAlt,
  FaCheckCircle,
  FaCalendarAlt,
  FaTimesCircle,
  FaClock,
  FaExclamationTriangle,
  FaPause,
  FaUndo,
  FaTable,
  FaUser,
} from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { HiViewGrid } from "react-icons/hi";

import api from "../../../../api";
import { useUser } from "../../../../authentication/authentication";
import {
  apiRequest,
  apiSuccess,
  apiFetchError,
} from "../../../../redux/api/apiActions";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import formatDate from "../../../../utils/formatDate";
import Pagination from "../../table/Pagination";
import FilterSide from "../../../common/FilterSide";
import { use } from "react";
import { useNavigate } from "react-router-dom";
import Txt from "../../../Text";
import { arClass } from "../../../../utils/useArClass";
import textTranslator from "../../../../utils/useTextTranslator";
import typeTranslator from "../../../../utils/useTypeTranslator";
import formatTime from "../../../../utils/formatTime";
import { Link } from "lucide-react";

const Text = ({ children, className, page = "VISITS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

// Visit Skeleton Component
const VisitSkeleton = ({ darkMode }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className={arClass(
      `relative rounded-xl border-2 p-4 overflow-hidden ${
        darkMode
          ? "bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700"
          : "bg-gradient-to-br from-white to-gray-50 border-gray-100"
      }`
    )}
  >
    {/* Animated Shimmer Effect */}
    <motion.div
      className={arClass(
        "absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
      )}
      animate={{
        x: ["0%", "100%"],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut",
      }}
    />

    {/* Header Section Skeleton */}
    <div className={arClass("flex items-center gap-3 mb-3 relative z-20")}>
      <motion.div
        className={arClass("relative")}
        animate={{ opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <div
          className={arClass(
            `w-12 h-12 rounded-full ${darkMode ? "bg-gray-700" : "bg-gray-200"}`
          )}
        />
      </motion.div>
      <div className={arClass("space-y-2 flex-1")}>
        <motion.div
          className={arClass(
            `h-4 w-32 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`
          )}
          animate={{ opacity: [0.6, 0.9, 0.6] }}
          transition={{ duration: 1.5, repeat: Infinity, delay: 0.1 }}
        />
        <motion.div
          className={arClass(
            `h-3 w-24 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`
          )}
          animate={{ opacity: [0.6, 0.9, 0.6] }}
          transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
        />
        <motion.div
          className={arClass(
            `h-3 w-20 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`
          )}
          animate={{ opacity: [0.6, 0.9, 0.6] }}
          transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
        />
      </div>
    </div>

    {/* Visit Details Skeleton */}
    <div className={arClass("space-y-2 text-sm relative z-20")}>
      {[...Array(4)].map((_, i) => (
        <motion.div
          key={i}
          className={arClass("flex items-center justify-between")}
          animate={{ opacity: [0.6, 0.9, 0.6] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            delay: i * 0.1,
          }}
        >
          <div
            className={arClass(
              `h-3 w-16 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`
            )}
          />
          <div
            className={arClass(
              `h-3 w-20 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`
            )}
          />
        </motion.div>
      ))}
    </div>

    {/* Enhanced Shadow */}
    <div
      className={arClass("absolute inset-0 rounded-xl shadow-lg opacity-20")}
    />
  </motion.div>
);

// Empty Visit State Component
const EmptyVisitState = ({
  darkMode,
  colors,
  hasActiveFilters,
  clearAllFilters,
  setVisitSearchQuery,
  visitSearchQuery,
  selectedPatient,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={arClass(
        "flex flex-col items-center justify-center py-12 px-6 text-center w-full"
      )}
    >
      {/* Animated Icon Container */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          delay: 0.2,
          type: "spring",
          stiffness: 200,
        }}
        className={arClass("relative mb-6")}
      >
        {/* Background Gradient */}
        <div
          className={arClass(
            `absolute inset-0 rounded-2xl transform rotate-6 scale-110 ${
              darkMode
                ? "bg-gradient-to-br from-blue-900/20 to-indigo-900/20"
                : "bg-gradient-to-br from-blue-50 to-indigo-100"
            }`
          )}
        ></div>

        {/* Main Icon */}
        <div
          className={arClass(
            `relative rounded-xl p-6 shadow-lg border ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-100"
            }`
          )}
        >
          <div
            className={arClass(
              "w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-2"
            )}
          >
            <FaCalendarCheck className={arClass("w-8 h-8 text-white")} />
          </div>
        </div>

        {/* Floating Elements */}
        <motion.div
          animate={{
            y: [0, -10, 0],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className={arClass(
            `absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg ${
              darkMode ? "bg-yellow-500" : "bg-yellow-400"
            }`
          )}
        >
          <svg
            className={arClass("w-4 h-4 text-white")}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </motion.div>

        <motion.div
          animate={{
            y: [0, -8, 0],
            rotate: [0, -5, 0],
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5,
          }}
          className={arClass(
            `absolute -bottom-2 -left-2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg ${
              darkMode ? "bg-green-500" : "bg-green-400"
            }`
          )}
        >
          <svg
            className={arClass("w-3 h-3 text-white")}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </motion.div>
      </motion.div>

      {/* Text Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className={arClass("max-w-md")}
      >
        <h3
          className={arClass(
            `text-sm sm:text-lg font-bold mb-2 ${colors.text.primary}`
          )}
        >
          {!selectedPatient ? (
            <Text>noPatientSelected</Text>
          ) : visitSearchQuery || hasActiveFilters ? (
            <Text>noVisitsFound</Text>
          ) : (
            <Text>No Visits Yet</Text>
          )}
        </h3>

        <p
          className={arClass(
            `text-xs sm:text-sm mb-6 ${colors.text.secondary}`
          )}
        >
          {!selectedPatient ? (
            <Text>selectPatientViewVisits</Text>
          ) : visitSearchQuery ? (
            <Text>noVisitsMatchSearch</Text>
          ) : hasActiveFilters ? (
            <Text>noVisitsMatchFilters</Text>
          ) : (
            <Text>noVisitsPatient</Text>
          )}
        </p>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className={arClass("flex flex-col sm:flex-row gap-3 justify-center")}
        >
          {(visitSearchQuery || hasActiveFilters) && (
            <motion.button
              onClick={() => {
                setVisitSearchQuery?.("");
                clearAllFilters?.();
              }}
              className={arClass(
                `px-6 py-3 rounded-lg font-semibold border transition-all duration-200 shadow-sm hover:shadow-lg text-xs sm:text-base ${
                  darkMode
                    ? "bg-red-800 border-red-700 hover:bg-red-700 hover:border-red-600 text-red-300"
                    : "bg-red-100 border-red-400 hover:bg-red-800 hover:border-red-300 text-red-800 hover:text-red-200"
                }`
              )}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {visitSearchQuery && hasActiveFilters ? (
                <Text>clearAll</Text>
              ) : visitSearchQuery ? (
                <Text>clearSearch</Text>
              ) : (
                <Text>clearFilters</Text>
              )}
            </motion.button>
          )}
        </motion.div>

        {/* Help Text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className={arClass(
            `mt-6 p-4 rounded-lg border ${
              darkMode
                ? "bg-blue-900/20 border-blue-700/30"
                : "bg-blue-50 border-blue-100"
            }`
          )}
        >
          <div className={arClass("flex items-start gap-3")}>
            <svg
              className={arClass(
                `w-5 h-5 mt-0.5 flex-shrink-0 ${
                  darkMode ? "text-blue-400" : "text-blue-500"
                }`
              )}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className={arClass("text-left")}>
              <p
                className={arClass(
                  `text-sm font-medium ${
                    darkMode ? "text-blue-300" : "text-blue-800"
                  }`
                )}
              >
                <Text>aboutVisits</Text>
              </p>
              <p
                className={arClass(
                  `text-xs ${darkMode ? "text-blue-400" : "text-blue-600"}`
                )}
              >
                {!selectedPatient ? (
                  <Text>selectPatientManageHistory</Text>
                ) : (
                  <Text>completedConsultationsPayment</Text>
                )}
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

const VisitSelection = ({
  darkMode,
  colors,
  onSelect,
  selectedPatient,
  pageSize = 6,
  selectedVisit,
}) => {
  const user = useUser();
  const visitsKey = `visits-selection`;
  const { requests } = useSelector((state) => state.api);
  const visitsLoading = requests[visitsKey]?.loading;
  const dispatch = useDispatch();
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage
  );

  // Visit state management
  const [visits, setVisits] = useState([]);
  const [visitPage, setVisitPage] = useState(1);
  const [visitTotalPages, setVisitTotalPages] = useState(0);
  const [visitTotalCount, setVisitTotalCount] = useState(0);
  const [visitSearchQuery, setVisitSearchQuery] = useState("");
  const [visitDebouncedSearch, setVisitDebouncedSearch] = useState("");

  // View mode: grid or table
  const [viewMode, setViewMode] = useState("grid");

  // Mobile view state
  const [mobileView, setMobileView] = useState("visits");

  // Filter states - using FilterSide structure
  const defaultFilters = {
    status: "All",
    visitType: "All",
    sort: "scheduledAt-desc",
    dateRange: "all",
  };
  const [filters, setFilters] = useState({ ...defaultFilters });
  const navigate = useNavigate();
  const isSearchingRef = useRef(false);
  const hasActiveFilters =
    filters.status !== defaultFilters.status ||
    filters.visitType !== defaultFilters.visitType ||
    filters.sort !== defaultFilters.sort ||
    filters.dateRange !== defaultFilters.dateRange;

  const clearAllFilters = () => {
    setFilters({ ...defaultFilters });
    setVisitSearchQuery("");
    setVisitPage(1);
  };

  // ✅ Reusable fetch function
  const fetchVisits = useCallback(async () => {
    if (!user?.id) return;

    let mounted = true;
    try {
      if (!isSearchingRef.current) {
        dispatch(apiRequest("GET", visitsKey));
      }

      const searchParam = visitDebouncedSearch
        ? `&q=${encodeURIComponent(visitDebouncedSearch)}`
        : "";
      const statusParam =
        filters.status !== "All" ? `&status=${filters.status}` : "";
      const sortParam = filters.sort ? `&sort=${filters.sort}` : "";

      const res = await api.get(
        `visits/patient/${selectedPatient.id}?page=${visitPage}&pageSize=${pageSize}${searchParam}${statusParam}${sortParam}&isPayment=1&isVisitType=1`
      );

      if (!mounted) return;

      setVisits(res.data.results || []);
      const totalCount = res.data.count ?? res.data.total ?? 0;

      setVisitTotalCount(totalCount);

      isSearchingRef.current = false;
      dispatch(apiSuccess("GET", visitsKey, "Fetched visits successfully"));
    } catch (err) {
      // Handle error silently
      if (!mounted) return;
      setVisits([]);
      isSearchingRef.current = false;
    }
  }, [user?.id, dispatch, visitPage, pageSize, filters]);
  // ✅ Main effect: runs on search, filters, pagination, etc.
  useEffect(() => {
    fetchVisits();
  }, [
    fetchVisits,
    visitDebouncedSearch,
    visitPage,
    pageSize,
    selectedPatient?.id,
    filters,
  ]);

  // Debounce search for visits
  useEffect(() => {
    const handler = setTimeout(() => {
      setVisitDebouncedSearch(visitSearchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [visitSearchQuery]);

  const handleSelect = (visit) => {
    if (onSelect) onSelect(visit);
    setMobileView("selected");
  };

  const clearSelected = () => {
    if (onSelect) onSelect(null);
    setMobileView("visits");
  };

  const getStatusLabel = (status) => {
    const lowerStatus = status?.toLowerCase();

    switch (lowerStatus) {
      case "completed":
        return {
          label: textTranslator("Completed", "VISITS"),
          style: darkMode
            ? "status !bg-green-900/30 !text-green-300 shadow-lg border border-green-700 !w-max text-xs"
            : "status !bg-green-50 !text-green-700 shadow-lg border border-green-200 !w-max text-xs",
          icon: <FaCheckCircle className={arClass("inline mr-1")} size={14} />,
        };
      case "scheduled":
        return {
          label: textTranslator("Scheduled", "VISITS"),
          style: darkMode
            ? "status !bg-blue-900/30 !text-blue-300 shadow-lg border border-blue-700 !w-max text-xs"
            : "status !bg-blue-50 !text-blue-700 shadow-lg border border-blue-200 !w-max text-xs",
          icon: <FaCalendarAlt className={arClass("inline mr-1")} size={14} />,
        };
      case "confirmed":
        return {
          label: textTranslator("Confirmed", "VISITS"),
          style: darkMode
            ? "status !bg-orange-900/30 !text-orange-300 shadow-lg border border-orange-700 !w-max text-xs"
            : "status !bg-orange-50 !text-orange-700 shadow-lg border border-orange-200 !w-max text-xs",
          icon: (
            <FaCalendarCheck className={arClass("inline mr-1")} size={14} />
          ),
        };
      case "cancelled":
        return {
          label: textTranslator("Cancelled", "VISITS"),
          style: darkMode
            ? "status !bg-red-900/30 !text-red-300 shadow-lg border border-red-700 !w-max text-xs"
            : "status !bg-red-50 !text-red-700 shadow-lg border border-red-200 !w-max text-xs",
          icon: <FaTimesCircle className={arClass("inline mr-1")} size={14} />,
        };
      default:
        return {
          label: status,
          style: darkMode
            ? "bg-gray-700 text-gray-300"
            : "bg-gray-500 text-white",
          icon: <FaCalendarAlt className={arClass("inline mr-1")} size={14} />,
        };
    }
  };

  const getPaymentStatusLabel = (payment) => {
    if (!payment) {
      return {
        label: textTranslator("Unpaied", "PAYMENTS"),
        style: darkMode
          ? "status !bg-gray-700 !text-gray-300 shadow-lg border border-gray-600 !w-max text-xs"
          : "status !bg-gray-300 !text-gray-900 shadow-lg border border-gray-900/40 !w-max text-xs",
        icon: <FaMoneyBillWave className={arClass("inline mr-1")} size={14} />,
      };
    }

    const lowerStatus = (payment.paymentStatus || "").toLowerCase();

    switch (lowerStatus) {
      case "paid":
      case "completed":
        return {
          label: textTranslator("Paid", "PAYMENTS"),
          style: darkMode
            ? "status !bg-green-900/30 !text-green-300 shadow-lg border border-green-700 !w-max text-xs"
            : "status !bg-green-50 !text-green-700 shadow-lg border border-green-200 !w-max text-xs",
          icon: <FaCheckCircle className={arClass("inline mr-1")} size={14} />,
        };

      case "pending":
        return {
          label: textTranslator("Pending", "PAYMENTS"),
          style: darkMode
            ? "status !bg-orange-900/30 !text-orange-300 shadow-lg border border-orange-700 !w-max text-xs"
            : "status !bg-orange-50 !text-orange-700 shadow-lg border border-orange-200 !w-max text-xs",
          icon: <FaClock className={arClass("inline mr-1")} size={14} />,
        };

      case "failed":
        return {
          label: textTranslator("Failed", "PAYMENTS"),
          style: darkMode
            ? "status !bg-red-900/30 !text-red-300 shadow-lg border border-red-700 !w-max text-xs"
            : "status !bg-red-50 !text-red-700 shadow-lg border border-red-200 !w-max text-xs",
          icon: (
            <FaExclamationTriangle
              className={arClass("inline mr-1")}
              size={14}
            />
          ),
        };

      case "refunded":
        return {
          label: textTranslator("Refunded", "PAYMENTS"),
          style: darkMode
            ? "status !bg-indigo-900/30 !text-indigo-300 shadow-lg border border-indigo-700 !w-max text-xs"
            : "status !bg-indigo-50 !text-indigo-700 shadow-lg border border-indigo-200 !w-max text-xs",
          icon: <FaUndo className={arClass("inline mr-1")} size={14} />,
        };

      case "partially-paid":
      case "partially_paid":
      case "partial":
        return {
          label: textTranslator("Partially Paid", "PAYMENTS"),
          style: darkMode
            ? "status !bg-yellow-900/30 !text-yellow-300 shadow-lg border border-yellow-700 !w-max text-xs"
            : "status !bg-yellow-50 !text-yellow-700 shadow-lg border border-yellow-200 !w-max text-xs",
          icon: (
            <FaMoneyBillWave className={arClass("inline mr-1")} size={14} />
          ),
        };

      case "cancelled":
      case "canceled":
        return {
          label: textTranslator("Cancelled", "PAYMENTS"),
          style: darkMode
            ? "status !bg-red-900/30 !text-red-300 shadow-lg border border-red-700 !w-max text-xs"
            : "status !bg-red-50 !text-red-700 shadow-lg border border-red-200 !w-max text-xs",
          icon: <FaTimesCircle className={arClass("inline mr-1")} size={14} />,
        };

      case "on-hold":
      case "on_hold":
      case "hold":
        return {
          label: textTranslator("On Hold", "PAYMENTS"),
          style: darkMode
            ? "status !bg-gray-800/30 !text-gray-300 shadow-lg border border-gray-600 !w-max text-xs"
            : "status !bg-gray-100 !text-gray-800 shadow-lg border border-gray-200 !w-max text-xs",
          icon: <FaPause className={arClass("inline mr-1")} size={14} />,
        };

      default:
        return {
          label: payment.paymentStatus || textTranslator("Unknown", "PAYMENTS"),
          style: darkMode
            ? "bg-gray-700 text-gray-300"
            : "bg-gray-500 text-white",
          icon: (
            <FaMoneyBillWave className={arClass("inline mr-1")} size={14} />
          ),
        };
    }
  };

  const getMethod = (method) => {
    const methodConfig = {
      cash: {
        label: textTranslator("cash", "PAYMENTS"),
        value: "cash",
        icon: <FaMoneyBillWave className={arClass("inline mr-1")} size={14} />,
      },
      "credit-card": {
        label: textTranslator("Credit Card", "PAYMENTS"),
        value: "credit-card",
        icon: <FaCreditCard className={arClass("inline mr-1")} size={14} />,
      },
      "debit-card": {
        label: textTranslator("Debit Card", "PAYMENTS"),
        value: "debit-card",
        icon: <FaUniversity className={arClass("inline mr-1")} size={14} />,
      },
      insurance: {
        label: textTranslator("Insurance", "PAYMENTS"),
        value: "insurance",
        icon: <FaShieldAlt className={arClass("inline mr-1")} size={14} />,
      },
      "online-payment": {
        label: textTranslator("Online Payment", "PAYMENTS"),
        value: "online-payment",
        icon: <FaGlobe className={arClass("inline mr-1")} size={14} />,
      },
      other: {
        label: textTranslator("Other", "PAYMENTS"),
        value: "other",
        icon: <FaFileAlt className={arClass("inline mr-1")} size={14} />,
      },
    };

    const lowerMethod = method?.toLowerCase();
    const config = methodConfig[lowerMethod] || methodConfig.other;

    // Dark mode styles
    const darkModeStyles = {
      cash: "bg-green-900/30 text-green-300 border border-green-700",
      "credit-card": "bg-blue-900/30 text-blue-300 border border-blue-700",
      "debit-card": "bg-purple-900/30 text-purple-300 border border-purple-700",
      insurance: "bg-orange-900/30 text-orange-300 border border-orange-700",
      "online-payment": "bg-cyan-900/30 text-cyan-300 border border-cyan-700",
      other: "bg-gray-700 text-gray-300 border border-gray-600",
    };

    // Light mode styles
    const lightModeStyles = {
      cash: "bg-green-50 text-green-700 border border-green-200",
      "credit-card": "bg-blue-50 text-blue-700 border border-blue-200",
      "debit-card": "bg-purple-50 text-purple-700 border border-purple-200",
      insurance: "bg-orange-50 text-orange-700 border border-orange-200",
      "online-payment": "bg-cyan-50 text-cyan-700 border border-cyan-200",
      other: "bg-gray-50 text-gray-700 border border-gray-200",
    };

    const style = darkMode
      ? darkModeStyles[lowerMethod] || darkModeStyles.other
      : lightModeStyles[lowerMethod] || lightModeStyles.other;

    return {
      ...config,
      style: `${arClass(
        "flex items-center gap-1 p-2 rounded-full shadow-lg font-medium text-sm !w-max text-xs max-sm:text-xss max-sm:p-1.5"
      )} ${style}`,
    };
  };

  // Table View Component
  const TableRow = ({ visit, handleSelect }) => {
    const isSelected = selectedVisit?.id === visit.id;
    const defaultLanguage = useSelector(
      (state) => state.settings.defaultLanguage
    );
    const border = defaultLanguage === "ar" ? "border-r-4" : "border-l-4";

    return (
      <tr
        className={`cursor-pointer transition-colors duration-200 ${
          isSelected
            ? darkMode
              ? `bg-gradient-to-r from-blue-900/20 to-blue-900/10 border-blue-500 ${border}`
              : `bg-gradient-to-r from-blue-50 to-blue-100/50 border-blue-500 ${border}`
            : darkMode
            ? `hover:bg-gray-800/50 ${border} border-transparent`
            : `hover:bg-gray-50/80 ${border} border-transparent`
        }`}
        onClick={() => handleSelect(visit)}
      >
        <td className={arClass("px-2 py-2 w-8 border-r max-sm:text-xs")}>
          {visit.id}
        </td>
        <td className={arClass("py-4 px-4")}>
          <div className={arClass("flex items-center gap-3")}>
            <div
              className={arClass(
                `w-10 h-10 rounded-lg flex items-center justify-center ${
                  selectedVisit?.id === visit.id
                    ? darkMode
                      ? "bg-blue-500/20"
                      : "bg-blue-100"
                    : darkMode
                    ? "bg-gray-700/50"
                    : "bg-gray-100"
                }`
              )}
            >
              <FaCalendar
                className={
                  selectedVisit?.id === visit.id
                    ? darkMode
                      ? "text-blue-300"
                      : "text-blue-600"
                    : darkMode
                    ? "text-gray-400"
                    : "text-gray-500"
                }
                size={14}
              />
            </div>
            <div>
              <div
                className={arClass(
                  `font-semibold text-sm ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`
                )}
              >
                {visit.patient?.firstName} {visit.patient?.lastName}
              </div>
              <div
                className={arClass(
                  `text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`
                )}
              >
                {typeTranslator(visit.visitType?.name).translatedType}
              </div>
            </div>
          </div>
        </td>
        <td className={arClass("py-4 px-4")}>
          <div className={arClass("flex flex-col")}>
            <div
              className={arClass(
                `text-xs sm:text-sm font-medium ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`
              )}
            >
              {formatDate(visit.scheduledAt, false)}
            </div>
            <div
              className={arClass(
                `text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`
              )}
            >
              {visit.checkInTime ? formatTime(visit.checkInTime) : "—"}
            </div>
          </div>
        </td>
        <td className={arClass("py-4 px-4")}>
          <span
            className={arClass(
              `text-xs flex items-center gap-1 px-3 py-1 rounded-full max-sm:text-xs ${
                getStatusLabel(visit.status).style
              }`
            )}
          >
            {getStatusLabel(visit.status).icon}
            {getStatusLabel(visit.status).label}
          </span>
        </td>
        <td className={arClass("py-4 px-4")}>
          <span
            className={arClass(
              `text-xs flex items-center gap-1 px-3 py-1 max-sm:px-2 max-sm:py-1 rounded-full max-sm:text-xs ${
                getPaymentStatusLabel(visit.payment).style
              }`
            )}
          >
            {getPaymentStatusLabel(visit.payment).icon}
            {getPaymentStatusLabel(visit.payment).label}
          </span>
        </td>
      </tr>
    );
  };
  const TableView = () => {
    return (
      <div className={arClass("overflow-x-auto")}>
        <table className={arClass("min-w-[600px] w-full border-collapse")}>
          <thead className={arClass("px-1")}>
            <tr
              className={arClass(
                `border-b ${
                  darkMode
                    ? "border-gray-700 bg-gray-800"
                    : "border-gray-200 bg-gray-50"
                }`
              )}
            >
              <th
                className={arClass(
                  `py-3 px-4 text-xs font-medium w-8 ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`
                )}
              >
                <Text>ID</Text>
              </th>
              <th
                className={arClass(
                  `py-3 px-4 text-xs font-medium ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`
                )}
              >
                <Text>Visit</Text>
              </th>
              <th
                className={arClass(
                  `py-3 px-4 text-xs font-medium ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`
                )}
              >
                <Text>Date & Time</Text>
              </th>
              <th
                className={arClass(
                  `py-3 px-4 text-xs font-medium ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`
                )}
              >
                <Text>Status</Text>
              </th>
              <th
                className={arClass(
                  `py-3 px-4 text-xs font-medium ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`
                )}
              >
                <Text page="PAYMENTS">Payment</Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {visits.map((visit) => (
              <TableRow
                key={visit.id}
                visit={visit}
                handleSelect={handleSelect}
              />
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className={arClass("space-y-6 max-sm:space-y-0")}>
      {/* Mobile Navigation Tabs */}
      <div className="hidden max-sm:block mb-4 rounded-lg">
        <div className={arClass("flex border rounded-lg overflow-hidden")}>
          <button
            onClick={() => setMobileView("visits")}
            className={`flex-1 py-3 text-center font-medium transition-colors text-xs  ${
              mobileView === "visits"
                ? darkMode
                  ? "bg-blue-600 text-white"
                  : "bg-blue-500 text-white"
                : darkMode
                ? "bg-gray-800 text-gray-300 hover:bg-gray-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }
                  ${defaultLanguage === "ar" ? "rounded-r-lg" : "rounded-l-lg"}
                  `}
          >
            <Text>Visits List</Text>
          </button>
          <button
            onClick={() => selectedVisit && setMobileView("selected")}
            disabled={!selectedVisit}
            className={arClass(
              `flex-1 py-3 text-center font-medium transition-colors text-xs rounded-r-lg ${
                mobileView === "selected"
                  ? darkMode
                    ? "bg-blue-600 text-white"
                    : "bg-blue-500 text-white"
                  : !selectedVisit
                  ? darkMode
                    ? "bg-gray-800 text-gray-500 cursor-not-allowed"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : darkMode
                  ? "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`
            )}
          >
            <Text>Selected Visit</Text>
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div
        className={arClass(
          "flex items-center gap-4 max-sm:flex-col max-sm:items-start"
        )}
      >
        <FilterSide
          searchTerm={visitSearchQuery}
          onSearchChange={(val) => {
            setVisitSearchQuery(val);
            setVisitPage(1);
          }}
          filters={filters}
          onFilterChange={(key, value) => {
            setFilters((prev) => ({ ...prev, [key]: value }));
            setVisitPage(1);
          }}
          sortOptions={{ sorting: filters.sort }}
          onSortChange={(val) => {
            setFilters((prev) => ({ ...prev, sort: val }));
            setVisitPage(1);
          }}
          onClearFilters={clearAllFilters}
          darkMode={darkMode}
          searchPlaceholder={textTranslator("searchPlaceholder", "VISITS")}
          filterTitle={textTranslator("Filter Visits", "VISITS")}
          filterOptions={{
            sections: [
              {
                key: "status",
                label: textTranslator("Status", "VISITS"),
                type: "buttons",
                options: [
                  { value: "All", label: textTranslator("All", "VISITS") },
                  {
                    value: "scheduled",
                    label: textTranslator("Scheduled", "VISITS"),
                  },
                  {
                    value: "in-progress",
                    label: textTranslator("In Progress", "VISITS"),
                  },
                  {
                    value: "completed",
                    label: textTranslator("Completed", "VISITS"),
                  },
                  {
                    value: "cancelled",
                    label: textTranslator("Cancelled", "VISITS"),
                  },
                ],
              },
              {
                key: "visitType",
                label: textTranslator("Visit Type", "VISITS"),
                type: "buttons",
                options: [
                  { value: "All", label: textTranslator("All", "VISITS") },
                  {
                    value: "checkup",
                    label: textTranslator("Checkup", "VISITS"),
                  },
                  {
                    value: "followup",
                    label: textTranslator("Follow-up", "VISITS"),
                  },
                  {
                    value: "emergency",
                    label: textTranslator("Emergency", "VISITS"),
                  },
                  {
                    value: "routine",
                    label: textTranslator("Routine", "VISITS"),
                  },
                ],
              },
              {
                key: "sort",
                label: textTranslator("Sort By", "VISITS"),
                type: "sort",
                options: [
                  {
                    value: "scheduledAt-desc",
                    label: textTranslator("Newest First", "VISITS"),
                  },
                  {
                    value: "scheduledAt-asc",
                    label: textTranslator("Oldest First", "VISITS"),
                  },
                  {
                    value: "firstName-asc",
                    label: textTranslator("Patient (A-Z)", "VISITS"),
                  },
                  {
                    value: "firstName-desc",
                    label: textTranslator("Patient (Z-A)", "VISITS"),
                  },
                ],
              },
              {
                key: "columns",
                label: textTranslator("Visible Columns", "VISITS"),
                type: "columns",
              },
            ],
          }}
          filterLabels={{
            filterBy: textTranslator("Filter By", "VISITS"),
          }}
          visibleColumns={null}
          onToggleColumn={() => {}}
          viewMode={viewMode}
        />

        <div
          className={arClass(
            "flex justify-end my-4 max-sm:mt-0 max-sm:mb-4 max-sm:w-full"
          )}
        >
          <motion.a
            href="/create-visit"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={arClass(`flex items-center gap-2 px-4 py-2 sm:py-3 mr-4 rounded-lg font-medium border min-w-max text-sm
                                        transition-colors duration-200 shadow-sm hover:shadow-lg max-sm:flex-1 max-sm:justify-center max-sm:text-xs
                                        ${
                                          darkMode
                                            ? "!bg-green-700 text-green-200 border-green-600 border hover:!bg-green-600"
                                            : "!bg-green-300 text-green-800 border-green-400 border hover:!bg-green-100"
                                        }`)}
          >
            <Text>addNewVisit</Text>
          </motion.a>

          <div className="flex rounded-lg border">
            <button
              onClick={() => {
                setViewMode("table");
              }}
              className={`p-3 rounded-l-lg transition-colors max-sm:p-2 ${
                viewMode === "table"
                  ? "bg-blue-600 !text-white"
                  : darkMode
                  ? "!bg-gray-700 !text-gray-300 hover:!bg-gray-600"
                  : "!bg-gray-100 !text-gray-600 hover:!bg-gray-200"
              }`}
            >
              <FaTable size={20} />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-3 rounded-r-lg transition-colors max-sm:p-2 ${
                viewMode === "grid"
                  ? "bg-blue-600 !text-white"
                  : darkMode
                  ? "!bg-gray-700 !text-gray-300 hover:!bg-gray-600"
                  : "!bg-gray-100 !text-gray-600 hover:!bg-gray-200"
              }`}
            >
              <HiViewGrid size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Grid - follow AppointmentSelection layout */}
      <div
        className={arClass(
          "grid grid-cols-12 gap-6 max-sm:grid-cols-1 max-sm:gap-0"
        )}
      >
        {/* Left Side - Visits List */}
        <div
          className={arClass(
            `col-span-8 space-y-6 h-full flex flex-col max-sm:col-span-1 ${
              mobileView !== "visits" && "max-sm:hidden"
            }`
          )}
        >
          {visitsLoading ? (
            <div className={arClass("flex-1 h-full border rounded-lg p-6")}>
              <motion.div
                className={arClass(
                  `h-4 w-32 rounded mb-6 ${
                    darkMode ? "bg-gray-700" : "bg-gray-200"
                  }`
                )}
                animate={{ opacity: [0.6, 0.9, 0.6] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: 0.1 }}
              />
              <motion.div
                className={arClass("grid grid-cols-1 md:grid-cols-2 gap-4")}
              >
                {[...Array(6)].map((_, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <VisitSkeleton darkMode={darkMode} />
                  </motion.div>
                ))}
              </motion.div>
            </div>
          ) : visits.length > 0 && visits !== "No data available" ? (
            <div
              className={arClass(
                `flex-1 h-full rounded-lg ${
                  viewMode === "table" ? "border" : ""
                } transition-colors duration-300 ${colors.card} overflow-auto`
              )}
            >
              {viewMode === "grid" ? (
                <div
                  className={arClass(
                    "grid grid-cols-1 md:grid-cols-2 gap-4 p-2"
                  )}
                >
                  {visits.map((visit, i) => (
                    <motion.div
                      key={visit.id || i}
                      className={arClass(
                        `p-4 rounded-xl border-2 border-t-[15px] cursor-pointer transition-all duration-300 ${
                          selectedVisit?.id === visit.id
                            ? darkMode
                              ? "border-green-500 bg-green-500/10 shadow-lg"
                              : "border-green-500 bg-green-50 shadow-lg"
                            : visit.payment
                            ? darkMode
                              ? "border-red-500/50 bg-red-500/5 hover:border-red-400/70 hover:shadow-sm"
                              : "border-red-400 bg-red-50/80 hover:border-red-600 hover:shadow-sm"
                            : darkMode
                            ? "border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:shadow-sm"
                            : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                        }`
                      )}
                      onClick={() => handleSelect(visit)}
                      whileHover={{
                        scale: 1.02,
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {/* Payment Warning Banner */}
                      {visit.payment && (
                        <div
                          className={arClass(
                            `mb-3 p-2 rounded-lg text-center ${
                              selectedVisit?.id === visit.id
                                ? darkMode
                                  ? "bg-green-500/20"
                                  : "bg-green-100"
                                : darkMode
                                ? "bg-red-500/20"
                                : "bg-red-100"
                            }`
                          )}
                        >
                          <div
                            className={arClass(
                              "flex items-center justify-center gap-2"
                            )}
                          >
                            <FaMoneyBillWave
                              className={
                                selectedVisit?.id === visit.id
                                  ? "text-green-500"
                                  : darkMode
                                  ? "text-red-400"
                                  : "text-red-600"
                              }
                            />
                            <span
                              className={arClass(
                                `text-sm font-semibold ${
                                  selectedVisit?.id === visit.id
                                    ? "text-green-500"
                                    : darkMode
                                    ? "text-red-400"
                                    : "text-red-600"
                                }`
                              )}
                            >
                              <Text>Payment Processed</Text>
                            </span>
                          </div>
                        </div>
                      )}

                      <div className={arClass("flex items-center gap-3 mb-3")}>
                        <div
                          className={arClass(
                            `w-12 h-12 rounded-full flex items-center justify-center ${
                              selectedVisit?.id === visit.id
                                ? darkMode
                                  ? "bg-green-500/20"
                                  : "bg-green-100"
                                : visit.payment
                                ? darkMode
                                  ? "bg-red-500/20"
                                  : "bg-red-100"
                                : darkMode
                                ? "bg-blue-500/20"
                                : "bg-blue-100"
                            }`
                          )}
                        >
                          <FaCalendar
                            className={
                              selectedVisit?.id === visit.id
                                ? "text-green-500"
                                : visit.payment
                                ? "text-red-500"
                                : "text-blue-500"
                            }
                            size={16}
                          />
                        </div>
                        <div className={arClass("flex-1 min-w-0")}>
                          <h4
                            className={arClass(
                              `font-semibold truncate ${colors.text.primary}`
                            )}
                          >
                            <Text>Visit</Text> #{visit.id}
                          </h4>
                          <p
                            className={arClass(
                              `text-xs truncate ${colors.text.muted}`
                            )}
                          >
                            {formatDate(visit.scheduledAt, false)}
                          </p>
                          <p
                            className={arClass(
                              `text-xs truncate ${colors.text.muted}`
                            )}
                          >
                            {typeTranslator(visit.visitType?.name)
                              .translatedType || "—"}
                          </p>
                        </div>
                        {selectedVisit?.id === visit.id && (
                          <div
                            className={arClass(
                              `p-1 rounded-full ${
                                darkMode ? "bg-green-500/20" : "bg-green-100"
                              }`
                            )}
                          >
                            <FaCheck
                              className={arClass("text-green-500")}
                              size={12}
                            />
                          </div>
                        )}
                      </div>

                      <div className={arClass("space-y-2")}>
                        
                        <div
                          className={arClass(
                            "flex items-center justify-between"
                          )}
                        >
                          <span
                            className={arClass(`text-xs ${colors.text.muted}`)}
                          >
                            <Text>Created By</Text>
                          </span>
                          <span
                            className={arClass(
                              `text-sm font-medium ${colors.text.primary}`
                            )}
                          >
                            {visit.creator.firstName} {visit.creator.lastName} (
                            <Text page ="ROLES">{visit.creator.role}</Text>)
                          </span>
                        </div>
                        <div
                          className={arClass(
                            "flex items-center justify-between"
                          )}
                        >
                          <span
                            className={arClass(`text-xs ${colors.text.muted}`)}
                          >
                            <Text>Visit Status</Text>
                          </span>
                          <span
                            className={arClass(
                              `text-sm flex items-center rounded-full p-1 w-full gap-1 ${
                                getStatusLabel(visit.status).style
                              }`
                            )}
                          >
                            {getStatusLabel(visit.status).icon}
                            {getStatusLabel(visit.status).label}
                          </span>
                        </div>
                        <div
                          className={arClass(
                            "flex items-center justify-between"
                          )}
                        >
                          <span
                            className={arClass(`text-xs ${colors.text.muted}`)}
                          >
                            <Text page="PAYMENTS">Payment Status</Text>
                          </span>
                          <span
                            className={arClass(
                              `text-sm flex items-center rounded-full p-1 w-full gap-1 max-sm:px-2 max-sm:py-1 max-sm:text-xs ${
                                getPaymentStatusLabel(visit.payment).style
                              }`
                            )}
                          >
                            {getPaymentStatusLabel(visit.payment).icon}
                            {getPaymentStatusLabel(visit.payment).label}
                          </span>
                        </div>
                        {visit.payment && (
                          <div
                            className={arClass(
                              "flex items-center justify-between"
                            )}
                          >
                            <span
                              className={arClass(
                                `text-xs ${colors.text.muted}`
                              )}
                            >
                              <Text>Amount</Text>
                            </span>
                            <span
                              className={arClass(
                                `text-sm font-medium ${colors.text.primary}`
                              )}
                            >
                              ${visit.payment.amount || "0.00"}
                            </span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <TableView />
              )}

              {/* Pagination */}
              {visitTotalCount > pageSize && (
                <div className={arClass("mt-6 flex justify-center")}>
                  <Pagination
                    totalPages={visitTotalPages}
                    page={visitPage}
                    setPage={setVisitPage}
                    pageSize={pageSize}
                    totalPagesCount={visitTotalCount}
                  />
                </div>
              )}
            </div>
          ) : (
            <EmptyVisitState
              darkMode={darkMode}
              colors={colors}
              hasActiveFilters={hasActiveFilters}
              clearAllFilters={clearAllFilters}
              setVisitSearchQuery={setVisitSearchQuery}
              visitSearchQuery={visitSearchQuery}
              selectedPatient={selectedPatient}
            />
          )}
        </div>

        {/* Right Side - Selected Visit Card */}
        <div
          className={arClass(
            `col-span-4 h-full ${
              mobileView !== "selected" && "max-sm:hidden"
            } ${visitsLoading ? "max-h-[calc(100%)]" : "h-full"}`
          )}
        >
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className={arClass(
              `h-full flex flex-col rounded-2xl border-2 bg-gradient-to-br max-sm:rounded-xl max-sm:border ${
                darkMode
                  ? "from-blue-900/20 to-cyan-900/20 border-blue-400/30"
                  : "from-blue-50 to-cyan-50 border-blue-200"
              } shadow-xl overflow-hidden`
            )}
          >
            <div className={arClass("flex justify-end w-full p-2")}>
              <motion.button
                onClick={() => clearSelected()}
                className={arClass(
                  `p-2 rounded-lg ${
                    darkMode
                      ? "bg-red-500/20 hover:bg-red-500/30"
                      : "bg-red-50 hover:bg-red-100"
                  } transition-colors`
                )}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <IoMdClose
                  className={darkMode ? "text-red-400" : "text-red-500"}
                />
              </motion.button>
            </div>
            {selectedVisit ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className={arClass("flex-1 overflow-auto")}
              >
                <div className={arClass("p-6 max-sm:p-4")}>
                  <div className={arClass("flex items-center gap-3")}>
                    <div
                      className={arClass(
                        `sm:w-14 sm:h-14 w-10 h-10 rounded-full flex items-center justify-center ${
                          selectedVisit.payment
                            ? darkMode
                              ? "bg-red-600/30"
                              : "bg-red-100"
                            : darkMode
                            ? "bg-blue-600/30"
                            : "bg-blue-100"
                        }`
                      )}
                    >
                      <FaCalendar
                        className={
                          selectedVisit.payment
                            ? darkMode
                              ? "text-red-300"
                              : "text-red-600"
                            : colors.text.muted
                        }
                        size={20}
                      />
                    </div>
                    <div>
                      <h3
                        className={arClass(
                          `sm:text-xl text-lg font-bold ${colors.text.primary}`
                        )}
                      >
                        <Text>Selected Visit</Text>
                      </h3>
                      <div className={arClass("flex gap-2")}>
                        <p
                          className={arClass(
                            `sm:text-sm text-xs ${colors.text.secondary} font-medium`
                          )}
                        >
                          {formatDate(selectedVisit.scheduledAt, false)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Warning */}
                {selectedVisit.payment && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={arClass(
                      `mb-4 p-4 rounded-xl border-2 mx-4 ${
                        darkMode
                          ? "bg-red-500/10 border-red-500/30"
                          : "bg-red-50 border-red-200"
                      }`
                    )}
                  >
                    <div className={arClass("flex items-center gap-3")}>
                      <FaMoneyBillWave
                        className={arClass(
                          `text-lg ${
                            darkMode ? "text-red-300" : "text-red-600"
                          }`
                        )}
                      />
                      <div>
                        <h4
                          className={arClass(
                            `font-semibold ${
                              darkMode ? "text-red-300" : "text-red-700"
                            }`
                          )}
                        >
                          <Text>Payment Processed</Text>
                        </h4>
                        <p
                          className={arClass(
                            `text-sm ${
                              darkMode ? "text-red-400" : "text-red-600"
                            }`
                          )}
                        >
                          <Text>visitAlreadyPaymentRecord</Text>
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Visit Details */}
                <div className={arClass("space-y-4 p-2")}>
                  <div className={arClass("grid grid-cols-2 gap-3")}>
                    <div
                      className={arClass(
                        `p-3 rounded-xl ${
                          darkMode ? "bg-cyan-800/30" : "bg-white"
                        } border ${
                          darkMode ? "border-cyan-700/50" : "border-cyan-100"
                        }`
                      )}
                    >
                      <p
                        className={arClass(
                          `text-xs font-semibold uppercase tracking-wide ${
                            darkMode ? "text-cyan-300" : "text-cyan-600"
                          } mb-1`
                        )}
                      >
                        <Text>Scheduled</Text>
                      </p>
                      <p
                        className={arClass(
                          `text-sm font-medium ${colors.text.primary}`
                        )}
                      >
                        {formatDate(selectedVisit.scheduledAt, false)}
                      </p>
                    </div>
                    <div
                      className={arClass(
                        `p-3 rounded-xl ${
                          darkMode ? "bg-purple-800/30" : "bg-white"
                        } border ${
                          darkMode
                            ? "border-purple-700/50"
                            : "border-purple-100"
                        }`
                      )}
                    >
                      <p
                        className={arClass(
                          `text-xs font-semibold uppercase tracking-wide ${
                            darkMode ? "text-purple-300" : "text-purple-600"
                          } mb-1`
                        )}
                      >
                        <Text>Doctor</Text>
                      </p>
                      <p
                        className={arClass(
                          `text-sm font-medium ${colors.text.primary}`
                        )}
                      >
                        {selectedVisit.doctor
                          ? `${textTranslator("Dr.", "VISITS")} ${
                              selectedVisit.doctor.firstName
                            } ${selectedVisit.doctor.lastName}`
                          : "—"}
                      </p>
                    </div>
                  </div>

                  <div className={arClass("grid grid-cols-2 gap-3")}>
                    <div
                      className={arClass(
                        `p-3 rounded-xl ${
                          darkMode ? "bg-green-800/30" : "bg-white"
                        } border ${
                          darkMode ? "border-green-700/50" : "border-green-100"
                        }`
                      )}
                    >
                      <p
                        className={arClass(
                          `text-xs font-semibold uppercase tracking-wide ${
                            darkMode ? "text-green-300" : "text-green-600"
                          } mb-1`
                        )}
                      >
                        <Text>Check In</Text>
                      </p>
                      <p
                        className={arClass(
                          `text-sm font-medium ${colors.text.primary}`
                        )}
                      >
                        {selectedVisit.checkInTime
                          ? formatTime(selectedVisit.checkInTime)
                          : "—"}
                      </p>
                    </div>
                    <div
                      className={arClass(
                        `p-3 rounded-xl ${
                          darkMode ? "bg-green-800/30" : "bg-white"
                        } border ${
                          darkMode ? "border-green-700/50" : "border-green-100"
                        }`
                      )}
                    >
                      <p
                        className={arClass(
                          `text-xs font-semibold uppercase tracking-wide ${
                            darkMode ? "text-green-300" : "text-green-600"
                          } mb-1`
                        )}
                      >
                        <Text>Check Out</Text>
                      </p>
                      <p
                        className={arClass(
                          `text-sm font-medium ${colors.text.primary}`
                        )}
                      >
                        {selectedVisit.checkOutTime
                          ? formatTime(selectedVisit.checkOutTime)
                          : "—"}
                      </p>
                    </div>
                  </div>

                  {selectedVisit.payment && (
                    <div
                      className={arClass(
                        `p-3 rounded-xl ${
                          darkMode ? "bg-red-800/30" : "bg-red-50"
                        } border ${
                          darkMode ? "border-red-700/50" : "border-red-200"
                        }`
                      )}
                    >
                      <p
                        className={arClass(
                          `text-xs font-semibold uppercase tracking-wide ${
                            darkMode ? "text-red-300" : "text-red-600"
                          } mb-1`
                        )}
                      >
                        <Text>Payment Details</Text>
                      </p>
                      <div className={arClass("space-y-2")}>
                        <div className={arClass("flex justify-between")}>
                          <span className={arClass("text-sm")}>
                            <Text>Amount</Text>:
                          </span>
                          <span className={arClass("text-sm font-medium")}>
                            ${selectedVisit.payment.amount || "0.00"}
                          </span>
                        </div>
                        <div className={arClass("flex justify-between")}>
                          <span className={arClass("text-sm")}>
                            <Text>Status</Text>:
                          </span>
                          <span
                            className={arClass(
                              `text-sm flex items-center rounded-full p-2 gap-1 w-full ${
                                getPaymentStatusLabel(selectedVisit.payment)
                                  .style
                              }`
                            )}
                          >
                            {getPaymentStatusLabel(selectedVisit.payment).icon}
                            {getPaymentStatusLabel(selectedVisit.payment).label}
                          </span>
                        </div>
                        <div className={arClass("flex justify-between")}>
                          <span className={arClass("text-sm")}>
                            <Text>Method</Text>:
                          </span>
                          <span
                           className={arClass(
                              `text-sm flex items-center rounded-full p-2 gap-1 ${
                                 getMethod(selectedVisit.payment.method).style
                              }`
                            )}
                         
                          >
                            {getMethod(selectedVisit.payment.method).icon}
                            {getMethod(selectedVisit.payment.method).label}
                          </span>
                        </div>
                      </div>
                      <motion.a
                        href={`/payments/${selectedVisit.payment.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className={arClass(
                          `max-sm:text-xs w-full mt-3 py-2 px-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                            darkMode
                              ? "bg-red-500/20 hover:bg-red-500/30 text-red-300"
                              : "bg-red-100 hover:bg-red-200 text-red-700"
                          }`
                        )}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <FaEye />
                        <Text>View Full Payment</Text>
                      </motion.a>
                    </div>
                  )}

                  <motion.div
                    className={arClass(
                      `p-4 rounded-xl mx-2 border ${
                        selectedVisit.payment
                          ? darkMode
                            ? "bg-yellow-500/10 border-yellow-500/20"
                            : "bg-yellow-50 border-yellow-200"
                          : darkMode
                          ? "bg-green-500/10 border-green-500/20"
                          : "bg-green-50 border-green-200"
                      }`
                    )}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div
                      className={arClass(
                        "flex items-center gap-3 max-sm:items-start"
                      )}
                    >
                      {selectedVisit.payment ? (
                        <FaMoneyBillWave
                          className={arClass(
                            `text-xs sm:text-lg  ${
                              darkMode ? "text-yellow-400" : "text-yellow-500"
                            }`
                          )}
                        />
                      ) : (
                        <FaCheck
                          className={arClass(
                            "text-green-500 text-xs sm:text-lg"
                          )}
                        />
                      )}
                      <div>
                        <span
                          className={arClass(
                            `text-xs sm:text-sm font-semibold block ${
                              selectedVisit.payment
                                ? darkMode
                                  ? "text-yellow-400"
                                  : "text-yellow-600"
                                : darkMode
                                ? "text-green-400"
                                : "text-green-600"
                            }`
                          )}
                        >
                          {selectedVisit.payment ? (
                            <Text>Payment Already Processed</Text>
                          ) : (
                            <Text>Ready to Proceed</Text>
                          )}
                        </span>
                        <span
                          className={arClass(
                            `text-xs ${
                              selectedVisit.payment
                                ? darkMode
                                  ? "text-yellow-300"
                                  : "text-yellow-500"
                                : darkMode
                                ? "text-green-300"
                                : "text-green-500"
                            }`
                          )}
                        >
                          {selectedVisit.payment ? (
                            <Text>visitDetailsPaymentLoaded</Text>
                          ) : (
                            <Text>visitDetailsLoaded</Text>
                          )}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ) : (
              <div
                className={arClass(
                  "p-6 max-sm:p-4 flex-1 flex flex-col justify-center items-center"
                )}
              >
                <motion.div
                  animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse",
                  }}
                  className={arClass("mb-6")}
                >
                  <FaCalendar
                    className={arClass(`w-16 h-16 ${colors.text.muted}`)}
                  />
                </motion.div>
                <motion.h4
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className={arClass(
                    `text-lg font-semibold mb-2 ${colors.text.primary}`
                  )}
                >
                  <Text>noVisitSelected</Text>
                </motion.h4>
                <motion.p
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className={arClass(
                    `text-sm text-center ${colors.text.secondary}`
                  )}
                >
                  <Text>selectVisitDetailsManagePayment</Text>
                </motion.p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default VisitSelection;
